import { type LogoutRequest, type User } from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';
import { loggedOut } from '@/shared/store/reducers/auth/auth.slice';

/**
 * The SESSION, as the shell needs it: who am I, and let me out.
 *
 * These two endpoints live in `shared/` rather than in `modules/Auth` because
 * the SHELL consumes them — `UserMenu` shows the operator's email and signs them
 * out on every page. W2 forbids `shared/` importing a module, and it is right to:
 * the shell's chrome cannot depend on a feature module, or the dependency runs
 * backwards and `Auth` can never be lazy-loaded.
 *
 * Signing IN is different and stays in `modules/Auth` — it is a surface with a
 * form and a page, used from exactly one route, and nothing in `shared/` needs it.
 *
 * `logout` clears the local session **whatever the server answers**. If the
 * refresh token was already revoked the call 401s, and refusing to sign out
 * locally would strand the operator signed-in at a shared workstation holding a
 * token the api will not honour anyway.
 */
export const sessionApi = createApi({
  reducerPath: 'sessionApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Me'],
  endpoints: (builder) => ({
    me: builder.query<User, void>({
      query: () => ({ url: '/me', method: 'GET' }),
      providesTags: ['Me'],
    }),

    /**
     * The refresh token is a PARAMETER, not read inside `query`: a `query`
     * function cannot reach state, and closing over the token from render is how
     * a rotated one gets presented — which the api reads as theft and answers by
     * revoking every session the actor has.
     */
    logout: builder.mutation<void, LogoutRequest>({
      query: (body) => ({ url: '/auth/logout', method: 'POST', body }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // The `catch` is load-bearing, not decoration: `try/finally` without
          // one RE-THROWS, and `onQueryStarted` has no caller — so a refused
          // logout (401 on an already-revoked token, the common case) surfaces
          // as an unhandled rejection instead of a clean sign-out. Found by the
          // mobile transposition's spec (P11b2a); the admin had no case for it.
        } finally {
          dispatch(loggedOut('signedOut'));
          EVENT_BUS.publish({ type: 'auth.loggedOut' });
        }
      },
    }),
  }),
});

export const { useMeQuery, useLogoutMutation } = sessionApi;
